import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { FormHelperText, Select, SelectMenuItem, SelectWrapper } from '@barracuda-internal/bds-core'
import { Controller } from 'react-hook-form'

// TODO: eliminate imports from "old" code paths (components/..., lib/...}, move things to V2
// V1 imports
import { useFormatMessage } from 'lib/localization'
import {
  FormRow,
  FormSpacer,
  FormText,
  HelperText,
  InlineRadioGroup,
  PageTitle
} from 'components/libs/layout/pages/pageLayout'
import timezones, { Timezone } from 'config/timezones'
import { GROUP_TITLE, TOPICS, useSupportModalLogic } from 'components/pages/support/useSupportModalLogic'
import UsersDefaultPolicySupport from 'components/pages/support/help/usersDefaultPolicy/UsersDefaultPolicy'
import { SettingsPageContainer } from 'components/libs/layout/pages/SettingsPageContainer'
import { useAppDispatch } from 'redux/toolkit/hooks'
import { setErrorSnackBar, setSuccessSnackBar } from 'redux/features/app/appSlice'
import WarningDialog from 'components/libs/dialog/warningDialog/WarningDialog'

// V2 module imports
import { useForm, useFormState, ManagedPolicy, ExemptSenders, useI18n, IEgdApiClient } from 'v2/modules/lib'
import { useIsReadonly } from 'lib/hooks/useIsReadonly'
import { IDefaultPolicyForm } from './defaultPolicyForm'

const BASE_I18N_KEY = 'ess.users.default_policy'
const DIALOG_I18N_KEY = 'ess.dialog'

export interface DefaultPolicyPageProps {
  settingsForm: IDefaultPolicyForm
  accountId: string
  domainId: number | null
  egdApiClient: IEgdApiClient
}

export const DefaultPolicyPage: React.FC<DefaultPolicyPageProps> = ({
  settingsForm,
  accountId,
  domainId,
  egdApiClient
}) => {
  const dispatch = useAppDispatch()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const formatDialogMessage = useFormatMessage(DIALOG_I18N_KEY)
  const [onOpenSupport, HelpModal] = useSupportModalLogic({
    content: UsersDefaultPolicySupport,
    title: TOPICS.defaultPolicy,
    groupTitle: GROUP_TITLE.userTopics
  })

  const { t } = useI18n()
  const canSetState = useRef(true)
  const isReadonly = useIsReadonly()
  const [isVerifyingDomainConflicts, setIsVerifyingDomainConflicts] = useState(false)
  const [isDomainConflictDialogOpen, setIsDomainConflictDialogOpen] = useState(false)
  const [numberOfConflictingDomains, setNumberOfConflictingDomains] = useState(0)
  const [isResetPending, setIsResetPending] = useState(false)
  const form = useForm({
    initialData: settingsForm.form.getInitialValues(),
    load: async () => settingsForm.form.load(),
    validate: async () => {
      // Not implemented yet
    },
    save: async (dirtyFormData: Record<string, any>) => {
      try {
        await settingsForm.form.save(dirtyFormData)
        dispatch(setSuccessSnackBar({ message: 'success_changing_settings' }))
      } catch (e) {
        dispatch(setErrorSnackBar({ message: 'error_changing_settings' }))
      }
    },
    schema: settingsForm.schema
  })

  const formState = useFormState(form)

  const isInProgress = useMemo(
    () => formState.isLoading || !formState.isReady,
    [formState.isLoading, formState.isReady]
  )

  // TODO: porting the module to a headless app cleans up extensive inline code in this UI component
  const onClickSave = useCallback(async () => {
    try {
      if (!domainId) {
        setIsVerifyingDomainConflicts(true)
        const domainModifiedSettings = await egdApiClient.accountService.getDomainModifiedSettings(accountId)
        const conflictingDomainIds: number[] = []
        Object.keys(formState.dirtyFields).forEach(inputName => {
          const inputAdapters = settingsForm.form.adapters.getByInputName(inputName)
          inputAdapters.forEach(adapter => {
            const nextList: number[] = domainModifiedSettings[adapter.variable] || []
            conflictingDomainIds.push(...nextList)
          })
        })
        if (!canSetState.current) {
          return
        }
        setIsVerifyingDomainConflicts(false)
        if (new Set(conflictingDomainIds).size) {
          setIsDomainConflictDialogOpen(true)
          setNumberOfConflictingDomains(new Set(conflictingDomainIds).size)
          return
        }
      }
      await form.save()
    } catch {
      if (!canSetState.current) {
        return
      }
      setIsVerifyingDomainConflicts(false)
      dispatch(setErrorSnackBar({ message: 'error_changing_settings' }))
    }
  }, [
    accountId,
    dispatch,
    domainId,
    egdApiClient.accountService,
    form,
    formState.dirtyFields,
    settingsForm.form.adapters
  ])

  useEffect(() => {
    canSetState.current = true
    return () => {
      canSetState.current = false
    }
  }, [])

  return useMemo(
    () => (
      <SettingsPageContainer
        isContentLoading={isInProgress}
        isFailedToLoad={formState.isFailed}
        dirtyCheck
        isDirtyForm={formState.isDirty}
      >
        <WarningDialog
          open={isDomainConflictDialogOpen}
          title={formatDialogMessage('domain_settings_conflict', { numberOfDomains: numberOfConflictingDomains })}
          description={formatDialogMessage('domain_settings_conflict_description')}
          onCancel={() => {
            setIsDomainConflictDialogOpen(false)
            setNumberOfConflictingDomains(0)
          }}
          onConfirm={async () => {
            await form.save()
            if (canSetState.current) {
              setIsDomainConflictDialogOpen(false)
              setNumberOfConflictingDomains(0)
            }
          }}
          isPending={formState.isSaving}
        />
        <HelpModal />
        <PageTitle
          title={formatMessage('title')}
          onOpenSupport={onOpenSupport}
          onCancel={() => setIsResetPending(true)}
          onReset={() => form.reset(formState.defaultValues)}
          isResetPending={isResetPending}
          closeWarningDialog={() => setIsResetPending(false)}
          onSubmit={onClickSave}
          submitInProgress={formState.isSaving || isVerifyingDomainConflicts}
          isDisabledSubmit={!formState.isDirty || isInProgress || settingsForm.form.isReadonly}
        />
        <FormText>{formatMessage('information_text')}</FormText>
        <FormSpacer />
        <FormRow contentIndent={4} rowTitle={formatMessage('default_policy_managed_users')}>
          <SelectWrapper variant="outlined">
            <Controller
              control={form.control}
              name="defaultManagedScanPolicy"
              render={({ field: { onChange, value }, fieldState }) => (
                <>
                  <Select
                    value={value}
                    onChange={onChange}
                    disabled={isInProgress || settingsForm.form.isReadonly || isReadonly}
                  >
                    {[ManagedPolicy.scan, ManagedPolicy.allow, ManagedPolicy.block].map((policy: ManagedPolicy) => (
                      <SelectMenuItem key={policy} value={policy}>
                        {formatMessage(`managed_policy_select.${policy}`)}
                      </SelectMenuItem>
                    ))}
                  </Select>
                  {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
                </>
              )}
            />
          </SelectWrapper>
        </FormRow>
        <FormSpacer />

        <FormRow contentIndent={4} rowTitle={formatMessage('default_policy_unmanaged_users')}>
          <SelectWrapper variant="outlined">
            <Controller
              control={form.control}
              name="defaultUnmanagedScanPolicy"
              render={({ field: { onChange, value }, fieldState }) => (
                <>
                  <Select
                    value={value}
                    onChange={onChange}
                    disabled={isInProgress || settingsForm.form.isReadonly || isReadonly}
                  >
                    {[ManagedPolicy.scan, ManagedPolicy.allow, ManagedPolicy.block].map((policy: ManagedPolicy) => (
                      <SelectMenuItem key={policy} value={policy}>
                        {formatMessage(`managed_policy_select.${policy}`)}
                      </SelectMenuItem>
                    ))}
                  </Select>
                  {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
                </>
              )}
            />
          </SelectWrapper>
        </FormRow>
        <FormSpacer />

        <FormRow contentIndent={4} rowTitle={formatMessage('exempt_senders')}>
          <SelectWrapper variant="outlined">
            <Controller
              control={form.control}
              name="defaultUserExemptEnabled"
              render={({ field: { onChange, value }, fieldState }) => (
                <>
                  <Select
                    value={value}
                    onChange={onChange}
                    disabled={isInProgress || settingsForm.form.isReadonly || isReadonly}
                  >
                    {[ExemptSenders.allow, ExemptSenders.partiallyAllow, ExemptSenders.block].map(policy => (
                      <SelectMenuItem key={policy} value={policy}>
                        {formatMessage(`exempt_senders_select.${policy}`)}
                      </SelectMenuItem>
                    ))}
                  </Select>
                  {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
                </>
              )}
            />
          </SelectWrapper>
        </FormRow>
        <FormRow contentIndent={4} rowTitle="">
          <FormSpacer />
          <HelperText>{formatMessage('exempt_senders_information_text')}</HelperText>
        </FormRow>
        <FormSpacer />

        <FormRow contentIndent={4} rowTitle={formatMessage('allow_users_to_block')}>
          <Controller
            control={form.control}
            name="defaultUserBlockEnabled"
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <InlineRadioGroup
                  name="defaultUserBlockEnabled"
                  onChange={onChange}
                  value={value}
                  disabled={isInProgress || settingsForm.form.isReadonly}
                />
                {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
              </>
            )}
          />
        </FormRow>
        <FormRow contentIndent={4} rowTitle="">
          <HelperText>{formatMessage('allow_users_to_block_information_text')}</HelperText>
        </FormRow>
        <FormSpacer />

        <FormRow contentIndent={4} rowTitle={formatMessage('allow_deliver_blocked_messages')}>
          <Controller
            control={form.control}
            name="defaultUserDeliverEnabled"
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <InlineRadioGroup
                  name="defaultUserDeliverEnabled"
                  onChange={onChange}
                  value={value}
                  disabled={isInProgress || settingsForm.form.isReadonly}
                />
                {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
              </>
            )}
          />
        </FormRow>
        <FormRow contentIndent={4} rowTitle="">
          <HelperText>{formatMessage('allow_deliver_blocked_messages_information_text')}</HelperText>
          <FormSpacer half />
          <HelperText>{formatMessage('allow_deliver_blocked_messages_information_text_2')}</HelperText>
        </FormRow>
        <FormSpacer />

        <FormRow contentIndent={4} rowTitle={formatMessage('allow_deliver_quarantined_messages')}>
          <Controller
            control={form.control}
            name="defaultUserQuarDeliverEnabled"
            render={({ field: { onChange, value }, fieldState }) => (
              <>
                <InlineRadioGroup
                  name="defaultUserQuarDeliverEnabled"
                  onChange={onChange}
                  value={value}
                  disabled={isInProgress || settingsForm.form.isReadonly}
                />
                {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
              </>
            )}
          />
        </FormRow>
        <FormRow contentIndent={4} rowTitle="">
          <HelperText>{formatMessage('allow_deliver_quarantined_messages_information_text')}</HelperText>
        </FormRow>
        <FormSpacer />

        <FormRow contentIndent={4} rowTitle={formatMessage('default_timezone')}>
          <SelectWrapper variant="outlined">
            <Controller
              control={form.control}
              name="defaultTimezone"
              render={({ field: { onChange, value }, fieldState }) => (
                <>
                  <Select
                    name="defaultTimezone"
                    onChange={onChange}
                    value={value}
                    disabled={isInProgress || settingsForm.form.isReadonly || isReadonly}
                  >
                    {timezones.map((timezone: Timezone) => {
                      const [tzValue, , utc] = timezone
                      return (
                        <SelectMenuItem key={tzValue} value={tzValue}>
                          {`(UTC${utc}) ${formatMessage(`timezones.${tzValue}`)}`}
                        </SelectMenuItem>
                      )
                    })}
                  </Select>
                  {fieldState.error && <FormHelperText error>{t(fieldState.error.message)}</FormHelperText>}
                </>
              )}
            />
          </SelectWrapper>
        </FormRow>

        <FormSpacer />
        <FormSpacer />
      </SettingsPageContainer>
    ),
    [
      isInProgress,
      formState.isFailed,
      formState.isDirty,
      formState.isSaving,
      formState.defaultValues,
      isDomainConflictDialogOpen,
      formatDialogMessage,
      numberOfConflictingDomains,
      HelpModal,
      formatMessage,
      onOpenSupport,
      isResetPending,
      onClickSave,
      isVerifyingDomainConflicts,
      settingsForm.form.isReadonly,
      form,
      t,
      isReadonly
    ]
  )
}
